import { utilsPlugin } from '@/plugins/utilsPlugin'
import { GameKeyEnum } from '@/types/enums'

const extraParams: Record<string, string> = {}
let launchTrackingStopped = false

export type LaunchMode = 'pwa' | 'browser'

// launched from the home screen shortcut (installed PWA) - display-mode standalone, or iOS Safari navigator.standalone
function getLaunchMode(): LaunchMode {
  if (import.meta.env.SSR || typeof window === 'undefined') {
    return 'browser'
  }
  const isStandalone = window.matchMedia?.('(display-mode: standalone)')?.matches
    || (window.navigator as Navigator & { standalone?: boolean })?.standalone === true
  return isStandalone ? 'pwa' : 'browser'
}

// VITE_DEBUG_GA='on' logs every GA event to the console and enables GA in dev mode (separate non-prod GA property);
// any other value ('off', empty, unset) disables both - compared explicitly, a non-empty 'off' string is truthy
function isDebugGa() {
  return import.meta.env.VITE_DEBUG_GA === 'on'
}

// whether gtag.js may be loaded (after cookie consent) - always in builds, in dev mode only with VITE_DEBUG_GA='on'
function canLoadGtag() {
  if (import.meta.env.SSR) {
    return false
  }
  return import.meta.env.MODE !== 'development' || isDebugGa()
}

// GA config-level params - set in createGtag config (routerInitPlugin.ts), so GA4 attaches them to every hit
// (gtag('set') params are not); also merged into the VITE_DEBUG_GA log to mirror what GA receives
function gaConfigParams() {
  return { gs_launch_mode: getLaunchMode() }
}

// sent once per visit (sessionStorage), only after cookie consent - called from onReadyVueGtag once gs_client_id is known
function gaLaunchEvents() {
  if (!utilsPlugin.siteGtag() || launchTrackingStopped) {
    return // GA not ready (no consent) or consent withdrawn in this visit - don't mark the visit as sent
  }
  try {
    if (sessionStorage.getItem('gs_launch_sent')) {
      return
    }
    sessionStorage.setItem('gs_launch_sent', '1')
  } catch {
    return
  }

  // platform / device: GA4 built-in dimensions (operating system, device category)
  const params: GaUtils.GaContext = {}
  if (extraParams.gs_launch_mode === 'pwa') {
    utilsPlugin.siteGtag()?.set('user_properties', { gs_has_pwa: 'yes' })
    try {
      if (!localStorage.getItem('gs_pwa_seen')) {
        localStorage.setItem('gs_pwa_seen', '1')
        gaEvent('app_first_pwa_launch', params)
      }
    } catch {
      // storage unavailable - skip first-launch marker
    }
  }
  gaEvent('app_launch', params)
}

// cookie consent withdrawn - remove the GA-only marker stored on the device and stop launch events for this visit
// (a pending client_id fallback timer must not write the marker back)
function clearLaunchMarkers() {
  launchTrackingStopped = true
  try {
    localStorage.removeItem('gs_pwa_seen')
  } catch {
    // storage unavailable - nothing to clear
  }
}

// cookie consent given again in the same tab (gtag already loaded -> optIn, no new 'script:loaded'):
// re-enable launch events and send them now if this visit hasn't sent them yet (deduped by gs_launch_sent)
// (gaLaunchEvents itself returns early until GA is ready)
function resumeLaunchTracking() {
  launchTrackingStopped = false
  if (extraParams.gs_client_id) {
    gaLaunchEvents()
  }
}

const CLIENT_ID_KEY = 'gs_client_id'
const CLIENT_ID_TIMEOUT_MS = 3000
// no cookie decision (or GA blocked) for this long -> our random id; consent given later keeps it (an id is never replaced)
const CLIENT_ID_UNDECIDED_MS = 15000
// btoa('Mozilla/5.0 (') - the old generator only encoded the user agent prefix, so these ids are shared by many users
const LEGACY_CLIENT_ID_PREFIX = 'TW96aWxsYS81LjAgK'

let clientId = ''
let clientIdFallbackTimer: ReturnType<typeof setTimeout> | undefined
const clientIdWaiters = new Set<() => void>()

function isLegacyClientId(id: string) {
  if (id.startsWith(LEGACY_CLIENT_ID_PREFIX)) {
    return true
  }
  try {
    return id === btoa(navigator.userAgent).slice(0, 22)
  } catch {
    return false // non-Latin1 user agent - the old generator threw there, so no legacy id exists
  }
}

// gs_client_id - per-device id: GA event param and session_id of GOAT API unlogged/alive; '' until established
// (stored also without GA consent - it was the A/B tests session id; whether to keep that is an open question)
function getClientId(): string {
  if (!clientId && !import.meta.env.SSR) {
    try {
      const stored = localStorage.getItem(CLIENT_ID_KEY) || ''
      if (stored && !isLegacyClientId(stored)) {
        clientId = stored
        extraParams.gs_client_id = stored
      }
    } catch {
      // storage unavailable - wait for an in-memory id
    }
  }
  return clientId
}

// the only writer of gs_client_id - first id wins and is never replaced (stable per-device id)
function setClientId(id: string) {
  if (getClientId()) {
    return
  }
  clientId = id
  extraParams.gs_client_id = id
  try {
    localStorage.setItem(CLIENT_ID_KEY, id)
  } catch {
    // storage unavailable - id kept in memory for this page load
  }
  clearTimeout(clientIdFallbackTimer)
  clientIdFallbackTimer = undefined
  const waiters = [...clientIdWaiters]
  clientIdWaiters.clear()
  waiters.forEach(callback => callback())
}

// run the callback once an id exists - now, or after the cookie decision (GA client_id / our id) or the 15 s fallback;
// the same callback is registered only once (several pages call goadLoadAliveData while waiting)
function whenClientId(callback: () => void) {
  if (import.meta.env.SSR) {
    return
  }
  if (getClientId()) {
    callback()
    return
  }
  clientIdWaiters.add(callback)
  if (!clientIdFallbackTimer) {
    clientIdFallbackTimer = setTimeout(() => setClientId(generateDeviceId()), CLIENT_ID_UNDECIDED_MS)
  }
}

// cookie decision - stored (page load) or just made; with GA the id comes from onReadyVueGtag (GA client_id),
// without GA (refused, or dev mode without VITE_DEBUG_GA='on') our random id is set right away
function onConsentDecision(gaAllowed: boolean) {
  if (!gaAllowed || !canLoadGtag()) {
    setClientId(generateDeviceId())
  }
}

// random 22-char id - the previous btoa(userAgent + …).slice(0, 22) only encoded the UA prefix,
// so every browser with the same UA start got the same id (and btoa throws on non-Latin1 chars)
function generateDeviceId() {
  const uuid = globalThis.crypto?.randomUUID?.()
  if (uuid) {
    return uuid.replace(/-/g, '').slice(0, 22)
  }
  return Array.from({ length: 22 }, () => Math.floor(Math.random() * 36).toString(36)).join('')
}

// launchTrackingStopped is intentionally NOT reset here - only consent changes (clearLaunchMarkers / resumeLaunchTracking)
// toggle it, so consent withdrawn while the GA script was still loading stays respected
function onReadyVueGtag(gtagId: string) {
  extraParams.gs_launch_mode = getLaunchMode()
  if (getClientId() || typeof window?.gtag !== 'function') {
    setClientId(generateDeviceId()) // no-op when an id already exists
    gaLaunchEvents()
    return
  }
  let resolved = false
  // prefer the GA client_id; the gtag 'get' callback may never fire (e.g. blocked request) - fall back after a timeout
  const resolveClientId = (gaClientId: string) => {
    if (resolved) {
      return
    }
    resolved = true
    // no-op if an id was set meanwhile (15 s fallback); stored even if consent was withdrawn meanwhile (unlogged/alive session_id) -
    // launch events and gs_pwa_seen are guarded by launchTrackingStopped in gaLaunchEvents
    setClientId(gaClientId?.length ? gaClientId : generateDeviceId())
    gaLaunchEvents()
  }
  window.gtag('get', gtagId, 'client_id', resolveClientId)
  setTimeout(resolveClientId, CLIENT_ID_TIMEOUT_MS, '')
}

// sent params = the event's own context + store-wide params (gs_client_id, gs_launch_mode…)
type GaSentParams = GaUtils.GaContext & Record<string, unknown>

// params attached to every GA event sent by the store (gaEvent and the router page_view)
function gaCommonParams(params: GaUtils.GaContext): GaSentParams {
  let idUser = ''
  if (utilsPlugin.siteGtag() || isDebugGa()) {
    // ssg throw error when used gamesUtils
    if (useGameStore().token(GameKeyEnum.goat)?.id) {
      idUser = `${GameKeyEnum.goat}_${useGameStore().token(GameKeyEnum.goat)?.id}`
    } else if (params.gs_game && useGameStore().token(params.gs_game)?.id) {
      idUser = `${params.gs_game}_${useGameStore().token(params.gs_game)?.id}`
    }
  }
  return {
    ...params,
    ...extraParams,
    ...(params?.gs_user_id ? { gs_user_id: params.gs_user_id } : (idUser ? { gs_user_id: idUser } : {}))
  }
}

// VITE_DEBUG_GA console log - mirrors what GA receives (incl. GA config-level params)
function logDebugGa(eventName: string, params: GaUtils.GaContext) {
  if (isDebugGa()) {
    // eslint-disable-next-line no-console
    console?.log({ eventName, params: { ...gaConfigParams(), ...params } }, 'VITE_DEBUG_GA')
  }
}

function gaEvent(eventName: string, params: GaUtils.GaContext) {
  if (!utilsPlugin.siteGtag() && !isDebugGa()) {
    return // no consent / GA not loaded - nothing to send or log
  }
  const eventParams = gaCommonParams(params)
  utilsPlugin.siteGtag()?.event(eventName, eventParams)
  logDebugGa(eventName, eventParams)
}
enum gaSections {
  btnLogin = 'btnLogin',
  offersList = 'offersList',
  free = 'free',
  previewOffer = 'previewOffer',
  previewOffersList = 'previewOffersList',
  loginBanner = 'loginBanner',
  pageOffer = 'pageOffer',
  pageOffersList = 'pageOffersList',
  cart = 'cart',
  cartChecker = 'cartChecker',
  header = 'header',
  userBanner = 'userBanner',
  buyBtn = 'buyBtn',
  gaotPage = 'gaotPage',
  paymentDialog = 'paymentDialog',
  pageActivateGoatCode = 'pageActivateGoatCode',
  loyaltyGameBox = 'loyaltyGameBox',
  freeButton = 'freeButton'
}
enum gaLoginMethods {
  gameToken = 'gameToken',
  deepLink = 'deepLink',
  goat = 'goat',
  goatLink = 'goatLink',
  temporaryLink = 'temporaryLink'
}
enum gaHelperEnums {
  free = 'FREE'
}
export type LoginMethods = `${gaLoginMethods}`
interface ItemsContext {
  item_id?: string
  item_name?: string
  index?: number // position of the item in the list
  quantity?: number
  price?: number
}
export interface LoginContext extends GaUtils.GaContext {
  method?: string
  connectMode?: boolean
}
function gaLogin(params: LoginContext): void {
  const gsContext: string[] = []
  if (params.method) {
    gsContext.push(params.method)
  }
  gsContext.push(useGameStore().token(GameKeyEnum.goat)?.id ? 'goatAccount' : 'gameAccount')
  if (params?.gs_context) {
    gsContext.push(params.gs_context)
  }
  params.gs_context = gsContext.join(',')

  gaEvent('login', params)
}
export interface AddToCartContext extends GaUtils.GaContext {
  currency?: string
  value?: number
  items?: ItemsContext[]
}
function gaAddToCart(params: AddToCartContext): void {
  gaEvent('add_to_cart', params)
}

export interface RemoveFromCartContext extends GaUtils.GaContext {
  currency?: string
  value?: number
  items?: ItemsContext[]
}
function gaRemoveFromCart(params: RemoveFromCartContext) {
  gaEvent('remove_from_cart', params)
}
export interface ViewCartContext extends GaUtils.GaContext {
  currency?: string
  value?: number
  items?: ItemsContext[]
}
function gaViewCart(params: ViewCartContext) {
  gaEvent('view_cart', params)
}
function gaConnectBtnClick(params: GaUtils.GaContext) {
  gaEvent('connect_btn_click', params)
}
enum gaSignUpMethods {
  gmail = 'gmail',
  facebook = 'facebook',
  goatForm = 'goatForm'
}
export type SignUpMethods = `${gaSignUpMethods}` | string
export interface SignUpContext extends GaUtils.GaContext {
  method?: SignUpMethods
}
function gaSignUp(params: SignUpContext) {
  gaEvent('sign_up', params)
}
export interface PageViewContext extends GaUtils.GaContext {
  page_title?: string
  page_location?: string
  page_path?: string
}
// params for the router page_view (vue-gtag pageview() in routerInitPlugin.ts - the only page_view sender,
// it also handles UTM -> campaign); call only when GA is ready, the debug log shows what is sent
function gaPageViewParams(params: PageViewContext): GaSentParams {
  const pageViewParams = gaCommonParams(params)
  logDebugGa('page_view', pageViewParams)
  return pageViewParams
}
enum gaBuyBtnContext {
  goat_account = 'goat_account',
  game_account = 'game_account',
  connection_game_required = 'connection_game_required',
  not_logged_in = 'not_logged_in',
  offerExpired = 'offerExpired',
  offerBought = 'offerBought',
  offerLocked = 'offerLocked',
  offerFree = 'offerFree',
  showStorePaymentDialog = 'showStorePaymentDialog',
  beginCheckout = 'beginCheckout'
}
export interface BuyBtnClickContext extends GaUtils.GaContext {
  currency?: string
  value?: number
  items?: ItemsContext[]
}
function gaBuyBtnClick(params: BuyBtnClickContext): void {
  gaEvent('buy_btn_click', params)
}
function gaBeginCheckout(params: BuyBtnClickContext): void {
  gaEvent('begin_checkout', params)
}
function gaPurchase(params: BuyBtnClickContext): void {
  gaEvent('purchase', params)
}
export interface SpendVirtualCurrencyContext extends GaUtils.GaContext {
  virtual_currency_name?: string
  value?: number
  item_name?: string
  items?: ItemsContext[]
}
function gaViewItem(params: BuyBtnClickContext): void {
  gaEvent('view_item', params)
}
function gaVerifyBtnClick(params: GaUtils.GaContext) {
  gaEvent('verify_btn_click', params)
}
function gaChangeCommunication(params: GaUtils.GaContext) {
  gaEvent('change_communication', params)
}
function gaUsePromoCode(params: GaUtils.GaContext) {
  gaEvent('use_promo_code', params)
}
export type InstallMethod = 'native_prompt' | 'instructions' // browser install dialog (Chromium) / our "add to home screen" instructions
// install button clicked (game page or footer); gs_context = install method
function gaInstallBtnClick(params: GaUtils.GaContext, installMethod: InstallMethod) {
  gaEvent('install_btn_click', { ...params, gs_context: installMethod })
}
export type InstallOutcome = 'accepted' | 'dismissed' // userChoice of the browser install dialog
// user closed the browser install dialog (Chromium only); gs_context = outcome - the install funnel, installs are counted by app_installed
function gaInstallPromptResult(params: GaUtils.GaContext, outcome: InstallOutcome) {
  gaEvent('install_prompt_result', { ...params, gs_context: outcome })
}
// browser confirmed the app was installed ('appinstalled', Chromium only) - any install path, incl. the browser menu
function gaAppInstalled() {
  gaEvent('app_installed', {})
}
const gaUtils = {
  gaLogin,
  gaLoginMethods,
  gaSections,
  gaAddToCart,
  gaRemoveFromCart,
  gaViewCart,
  gaConnectBtnClick,
  gaSignUp,
  gaSignUpMethods,
  gaBuyBtnClick,
  gaBuyBtnContext,
  gaBeginCheckout,
  gaPurchase,
  gaHelperEnums,
  gaViewItem,
  onReadyVueGtag,
  getLaunchMode,
  gaConfigParams,
  isDebugGa,
  canLoadGtag,
  clearLaunchMarkers,
  resumeLaunchTracking,
  getClientId,
  whenClientId,
  onConsentDecision,
  gaVerifyBtnClick,
  gaChangeCommunication,
  gaUsePromoCode,
  gaInstallBtnClick,
  gaInstallPromptResult,
  gaAppInstalled,
  gaPageViewParams
}
export { gaUtils }
